import { useTranslation } from "react-i18next";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import IconClose from "@/assets/icons/close.svg?react";
import { sanitizeHtml } from "@/shared/utils/sanitizeHtml";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer.tsx";

interface HowToUseSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Admin-managed rich-text instructions per voucher (HTML). */
  content?: string | null;
  /** Optional fallback list of step strings if no admin HTML. */
  customSteps?: string[];
}

export default function HowToUseSheet({
  isOpen,
  onClose,
  content,
  customSteps,
}: HowToUseSheetProps) {
  const { t } = useTranslation();

  const steps =
    customSteps && customSteps.length > 0
      ? customSteps
      : [
          t("voucher_how_step_1") || "Kodni nusxa oling",
          t("voucher_how_step_2") ||
            "Partnerning do'koniga boring yoki saytini oching",
          t("voucher_how_step_3") ||
            "Kassada yoki to'lov paytida kodni ko'rsating",
          t("voucher_how_step_4") ||
            "Chegirma/mukofot avtomatik qo'llaniladi",
        ];

  return (
    <Drawer nested open={isOpen} onOpenChange={(e) => !e && onClose()}>
      <DrawerContent className="bg-gray-4 max-h-[85vh]" showTopThumb>
        <DrawerHeader className="flex justify-between items-center">
          <DrawerTitle className="text-lg">
            {t("voucher_how_to_use") || "Qanday ishlatish?"}
          </DrawerTitle>
          <DrawerClose>
            <IconClose className="size-6 text-gray-1 cursor-pointer" />
          </DrawerClose>
        </DrawerHeader>
        <DrawerBody className="overflow-y-auto">
          {content ? (
            // Same visual language as PartnerFAQSheet: a rounded card with the
            // orange accent + the FAQ answer rich-text styling.
            <div className="rounded-2xl border border-orange-100/30 bg-orange-100/[0.06] p-4 mb-4">
              <div
                className="text-sm text-white/85 leading-160 [&_a]:text-orange-100 [&_a]:underline [&_p]:mb-2 last:[&_p]:mb-0 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:leading-150 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mb-2 [&_strong]:text-white [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-3 mb-4">
              {steps.map((step, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-4 flex items-start gap-3"
                >
                  <span className="shrink-0 w-7 h-7 rounded-full bg-orange-100/15 border border-orange-100/30 flex-center text-orange-100 text-sm font-bold">
                    {idx + 1}
                  </span>
                  <p className="text-sm text-white/85 leading-150 pt-1">
                    {step}
                  </p>
                </div>
              ))}
            </div>
          )}
          <BaseButton
            text={t("ok") || "Tushunarli"}
            variant="secondary"
            className="w-full"
            onClick={onClose}
            vibrate
            vibrationType="light"
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
